import { useCallback, useRef, useSyncExternalStore } from 'react';

const buildBulkStateSignature = (api) => {
  if (!api) {
    return '';
  }

  return JSON.stringify({
    bulkMode: api.bulkMode,
    selectedLocationKeys: api.selectedLocationKeys,
    draftRoleByLocation: api.draftRoleByLocation,
    rolesLoading: api.rolesLoading,
    bulkSameRoleDraftId: api.bulkSameRoleDraftId,
    isBulkSaveDisabled: api.isBulkSaveDisabled,
    showDifferentRolesPanel: api.showDifferentRolesPanel,
    showSameRolePanel: api.showSameRolePanel,
    showSelectionBar: api.showSelectionBar,
  });
};

/**
 * Bridges bulk-bar hook APIs to sibling row components without prop drilling every callback.
 * Publishes a revision only when bulk UI state changes, not when the hook return object is recreated.
 */
export const useCanvasAccessBulkBridge = () => {
  const apiRef = useRef(null);
  const signatureRef = useRef('');
  const revisionRef = useRef(0);
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const getSnapshot = useCallback(() => revisionRef.current, []);

  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setBulkApi = useCallback((api) => {
    apiRef.current = api;

    const nextSignature = buildBulkStateSignature(api);
    if (nextSignature === signatureRef.current) {
      return;
    }

    signatureRef.current = nextSignature;
    revisionRef.current += 1;
    listenersRef.current.forEach((listener) => listener());
  }, []);

  return { bulkApi: apiRef.current, setBulkApi };
};

export default useCanvasAccessBulkBridge;
